import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../../services/api";

const BRAND_NAME = "Lifeline Multispeciality Hospital";
const THEME_COLOR = "#0a2a6a";

const updateMeta = (attr, key, value) => {
  if (!value) return;

  let el = document.querySelector(`meta[${attr}="${key}"]`);

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }

  el.setAttribute("content", value);
};

const updateLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);

  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }

  el.setAttribute("href", href);
};

const clearSchemas = () => {
  document
    .querySelectorAll('script[data-dynamic-seo="true"]')
    .forEach((s) => s.remove());
};

const updateSchemas = (schemas = []) => {
  clearSchemas();

  schemas.forEach((schema) => {
    const script = document.createElement("script");

    script.type = "application/ld+json";
    script.dataset.dynamicSeo = "true";
    script.text = JSON.stringify(schema);

    document.head.appendChild(script);
  });
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const loadSeo = async () => {
      const slug =
        location.pathname === "/"
          ? "home"
          : location.pathname.replace(/^\/+/, "");

      const canonical =
        window.location.origin + location.pathname;

      try {
        const res = await api.get(`/api/seo/${slug}`);

        if (!res.data?.success) return;

        const seo = res.data.data;

        const title = seo.meta_title
          ? `${seo.meta_title} | ${BRAND_NAME}`
          : BRAND_NAME;

        document.title = title;

        // Basic SEO
        updateMeta(
          "name",
          "description",
          seo.meta_description
        );

        updateMeta(
          "name",
          "keywords",
          seo.meta_keywords
        );

        updateMeta(
          "name",
          "robots",
          "index, follow"
        );

        updateMeta(
          "name",
          "author",
          BRAND_NAME
        );

        updateMeta(
          "name",
          "theme-color",
          THEME_COLOR
        );

        updateLink(
          "canonical",
          canonical
        );

        // Open Graph
        updateMeta(
          "property",
          "og:type",
          "website"
        );

        updateMeta(
          "property",
          "og:title",
          title
        );

        updateMeta(
          "property",
          "og:description",
          seo.meta_description
        );

        updateMeta(
          "property",
          "og:url",
          canonical
        );

        updateMeta(
          "property",
          "og:image",
          "/assets/img/og-default.jpg"
        );

        // Twitter
        updateMeta(
          "name",
          "twitter:card",
          "summary_large_image"
        );

        updateMeta(
          "name",
          "twitter:title",
          title
        );

        updateMeta(
          "name",
          "twitter:description",
          seo.meta_description
        );

        updateMeta(
          "name",
          "twitter:image",
          "/assets/img/og-default.jpg"
        );

        // Custom Meta Tags
        if (
          Array.isArray(seo.meta_tags) &&
          seo.meta_tags.length
        ) {
          seo.meta_tags.forEach((tag) => {
            if (!tag?.name) return;

            if (tag.name.startsWith("og:")) {
              updateMeta(
                "property",
                tag.name,
                tag.content
              );
            } else {
              updateMeta(
                "name",
                tag.name,
                tag.content
              );
            }
          });
        }

        // Schema Processing
        const schemaList = [];

        // Old schema_data support
        if (
          seo.schema_data &&
          Object.keys(seo.schema_data).length > 0
        ) {
          schemaList.push({
            "@context": "https://schema.org",
            "@type": "WebPage",
            url: canonical,
            name:
              seo.schema_data?.name ||
              title,
            description:
              seo.schema_data?.description ||
              seo.meta_description,
            ...seo.schema_data,
          });
        }

        // New schemas support
        if (
          Array.isArray(seo.schemas) &&
          seo.schemas.length
        ) {
          seo.schemas.forEach((item) => {
            try {
              const parsed = JSON.parse(
                item.json
              );

              schemaList.push(parsed);
            } catch (error) {
              console.error(
                "Invalid schema JSON:",
                item
              );
            }
          });
        }
        
        const BASE_URL = window.location.origin;

      const normalizeSchema = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(normalizeSchema);
        }

        if (obj && typeof obj === "object") {
          const result = {};

          Object.keys(obj).forEach((key) => {
            let value = obj[key];

            if (typeof value === "string") {
              // Convert /storage/... to absolute URL
              if (value.startsWith("/storage/")) {
                value = BASE_URL + value;
              }

              // Convert /assets/... if needed
              if (value.startsWith("/assets/")) {
                value = BASE_URL + value;
              }
            } else if (typeof value === "object") {
              value = normalizeSchema(value);
            }

            result[key] = value;
          });

          return result;
        }

        return obj;
      };

      updateSchemas(schemaList.map(normalizeSchema));

      } catch (err) {
        console.error(
          "SEO load failed",
          err
        );
      }
    };

    loadSeo();
  }, [location.pathname]);

  return null;
};

export default SeoManager;